/**
 * Curated AI-economy occupation list.
 *
 * Two tiers, aligned with the federal AI workforce push (White House AI Action
 * Plan, DOL AI Registered Apprenticeship initiative) and BLS 2024-34
 * Employment Projections:
 *   - ai_core: occupations that build, deploy, secure, or manage AI systems
 *   - ai_infrastructure: trades and engineering roles building the data
 *     centers, power, and manufacturing capacity behind AI (many no-degree)
 *
 * Growth percentages, 2024 national median wages, and education levels come
 * from the BLS 2024-34 Employment Projections (Table 1.2). Median wages act
 * as a fallback when live OEWS data is unavailable; live data always wins.
 *
 * This list is mirrored in the `ai_economy_occupations` Supabase table used
 * by the `api-top-occupations` edge function (type: "ai_economy"). Keep both
 * in sync when curating.
 */

export type AiEconomyTier = 'ai_core' | 'ai_infrastructure';

export interface AiEconomyOccupation {
  socCode: string;
  title: string;
  /** Must match createOccupationSlug(title) so salary-page links resolve. */
  slug: string;
  tier: AiEconomyTier;
  /** BLS projected percent employment change, 2024-34. */
  projectedGrowthPct: number;
  /** BLS 2024 national median annual wage (fallback display value). */
  medianWage2024: number;
  /** Typical education needed for entry (BLS). */
  education: string;
  /** Why this occupation is part of the AI economy. */
  note: string;
}

export const AI_ECONOMY_OCCUPATIONS: AiEconomyOccupation[] = [
  // ---- AI core: building and running AI systems ----
  {
    socCode: '15-2051',
    title: 'Data Scientists',
    slug: 'data-scientists',
    tier: 'ai_core',
    projectedGrowthPct: 33.5,
    medianWage2024: 112590,
    education: "Bachelor's degree",
    note: 'The fourth fastest-growing occupation in the U.S. economy, driven by demand to build and tune AI models.',
  },
  {
    socCode: '15-1212',
    title: 'Information Security Analysts',
    slug: 'information-security-analysts',
    tier: 'ai_core',
    projectedGrowthPct: 28.5,
    medianWage2024: 124910,
    education: "Bachelor's degree",
    note: 'AI systems and the data they train on are prime attack targets, fueling demand for security expertise.',
  },
  {
    socCode: '15-2011',
    title: 'Actuaries',
    slug: 'actuaries',
    tier: 'ai_core',
    projectedGrowthPct: 21.8,
    medianWage2024: 125770,
    education: "Bachelor's degree",
    note: 'AI-driven risk modeling expands what actuaries can price, from cyber risk to climate exposure.',
  },
  {
    socCode: '15-2031',
    title: 'Operations Research Analysts',
    slug: 'operations-research-analysts',
    tier: 'ai_core',
    projectedGrowthPct: 21.5,
    medianWage2024: 91290,
    education: "Bachelor's degree",
    note: 'Businesses adopting AI need analysts who can turn model output into operational decisions.',
  },
  {
    socCode: '15-1221',
    title: 'Computer and Information Research Scientists',
    slug: 'computer-and-information-research-scientists',
    tier: 'ai_core',
    projectedGrowthPct: 19.7,
    medianWage2024: 140910,
    education: "Master's degree",
    note: 'The researchers inventing the next generation of AI algorithms and architectures.',
  },
  {
    socCode: '15-1252',
    title: 'Software Developers',
    slug: 'software-developers',
    tier: 'ai_core',
    projectedGrowthPct: 15.8,
    medianWage2024: 133080,
    education: "Bachelor's degree",
    note: 'The largest projected job gain of any AI-related occupation: over 267,000 new positions by 2034.',
  },
  {
    socCode: '11-3021',
    title: 'Computer and Information Systems Managers',
    slug: 'computer-and-information-systems-managers',
    tier: 'ai_core',
    projectedGrowthPct: 15.2,
    medianWage2024: 171200,
    education: "Bachelor's degree",
    note: 'Every AI deployment needs leaders who can manage the systems, budgets, and teams behind it.',
  },
  {
    socCode: '15-1241',
    title: 'Computer Network Architects',
    slug: 'computer-network-architects',
    tier: 'ai_core',
    projectedGrowthPct: 11.9,
    medianWage2024: 130390,
    education: "Bachelor's degree",
    note: 'AI workloads are reshaping enterprise networks, from GPU clusters to low-latency data pipelines.',
  },
  {
    socCode: '15-1253',
    title: 'Software Quality Assurance Analysts and Testers',
    slug: 'software-quality-assurance-analysts-and-testers',
    tier: 'ai_core',
    projectedGrowthPct: 10.0,
    medianWage2024: 102610,
    education: "Bachelor's degree",
    note: 'AI quality assurance is one of the emerging roles highlighted by the Department of Labor.',
  },
  {
    socCode: '13-1111',
    title: 'Management Analysts',
    slug: 'management-analysts',
    tier: 'ai_core',
    projectedGrowthPct: 8.8,
    medianWage2024: 101190,
    education: "Bachelor's degree",
    note: 'Consultants who guide companies through AI adoption are in demand across every industry.',
  },
  {
    socCode: '15-1211',
    title: 'Computer Systems Analysts',
    slug: 'computer-systems-analysts',
    tier: 'ai_core',
    projectedGrowthPct: 8.7,
    medianWage2024: 103790,
    education: "Bachelor's degree",
    note: 'Integrating AI applications into existing business systems is core systems-analysis work.',
  },
  {
    socCode: '15-1243',
    title: 'Database Architects',
    slug: 'database-architects',
    tier: 'ai_core',
    projectedGrowthPct: 8.7,
    medianWage2024: 135980,
    education: "Bachelor's degree",
    note: 'AI models are only as good as the data infrastructure feeding them.',
  },

  // ---- AI infrastructure: building the physical AI buildout ----
  {
    socCode: '49-9081',
    title: 'Wind Turbine Service Technicians',
    slug: 'wind-turbine-service-technicians',
    tier: 'ai_infrastructure',
    projectedGrowthPct: 49.9,
    medianWage2024: 62580,
    education: 'Postsecondary nondegree award',
    note: 'The fastest-growing occupation in America: data centers are driving unprecedented demand for new power generation.',
  },
  {
    socCode: '47-2231',
    title: 'Solar Photovoltaic Installers',
    slug: 'solar-photovoltaic-installers',
    tier: 'ai_infrastructure',
    projectedGrowthPct: 42.1,
    medianWage2024: 51860,
    education: 'High school diploma or equivalent',
    note: 'AI data centers need massive new electricity supply, and solar is among the fastest capacity to deploy.',
  },
  {
    socCode: '49-9041',
    title: 'Industrial Machinery Mechanics',
    slug: 'industrial-machinery-mechanics',
    tier: 'ai_infrastructure',
    projectedGrowthPct: 16.1,
    medianWage2024: 63760,
    education: 'High school diploma or equivalent',
    note: 'Advanced manufacturing for chips and servers depends on mechanics who keep automated lines running.',
  },
  {
    socCode: '47-2111',
    title: 'Electricians',
    slug: 'electricians',
    tier: 'ai_infrastructure',
    projectedGrowthPct: 9.5,
    medianWage2024: 62350,
    education: 'High school diploma + apprenticeship',
    note: 'Every data center, chip fab, and grid upgrade in the AI buildout starts with electricians.',
  },
  {
    socCode: '11-9021',
    title: 'Construction Managers',
    slug: 'construction-managers',
    tier: 'ai_infrastructure',
    projectedGrowthPct: 8.7,
    medianWage2024: 106980,
    education: "Bachelor's degree",
    note: 'Managing the wave of data center and semiconductor plant construction across the country.',
  },
  {
    socCode: '49-9021',
    title: 'Heating, Air Conditioning, and Refrigeration Mechanics and Installers',
    slug: 'heating-air-conditioning-and-refrigeration-mechanics-and-installers',
    tier: 'ai_infrastructure',
    projectedGrowthPct: 8.1,
    medianWage2024: 59810,
    education: 'Postsecondary nondegree award',
    note: 'Cooling is the biggest operational challenge in AI data centers, and HVAC techs keep them running.',
  },
  {
    socCode: '17-2061',
    title: 'Computer Hardware Engineers',
    slug: 'computer-hardware-engineers',
    tier: 'ai_infrastructure',
    projectedGrowthPct: 7.3,
    medianWage2024: 155020,
    education: "Bachelor's degree",
    note: 'Designing the AI chips and server hardware behind the domestic semiconductor push.',
  },
  {
    socCode: '17-2071',
    title: 'Electrical Engineers',
    slug: 'electrical-engineers',
    tier: 'ai_infrastructure',
    projectedGrowthPct: 7.2,
    medianWage2024: 111910,
    education: "Bachelor's degree",
    note: 'Grid modernization and data center power systems are engineering bottlenecks for AI growth.',
  },
  {
    socCode: '49-9051',
    title: 'Electrical Power-Line Installers and Repairers',
    slug: 'electrical-power-line-installers-and-repairers',
    tier: 'ai_infrastructure',
    projectedGrowthPct: 6.6,
    medianWage2024: 92560,
    education: 'High school diploma or equivalent',
    note: 'A $90k+ no-degree job at the center of the grid expansion AI demands.',
  },
  {
    socCode: '47-2152',
    title: 'Plumbers, Pipefitters, and Steamfitters',
    slug: 'plumbers-pipefitters-and-steamfitters',
    tier: 'ai_infrastructure',
    projectedGrowthPct: 4.5,
    medianWage2024: 62970,
    education: 'High school diploma + apprenticeship',
    note: 'Liquid cooling systems in modern AI data centers are built and maintained by pipefitters.',
  },
];

const normalizeSoc = (soc: string): string => soc.trim().replace(/\./g, '-');

const bySoc = new Map(AI_ECONOMY_OCCUPATIONS.map((o) => [o.socCode, o]));
const bySlug = new Map(AI_ECONOMY_OCCUPATIONS.map((o) => [o.slug, o]));

const titleToSlug = (title: string): string =>
  title
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-');

/** Look up a curated AI-economy occupation by SOC code and/or title. */
export function getAiEconomyOccupation(socCode?: string, title?: string): AiEconomyOccupation | undefined {
  if (socCode) {
    const match = bySoc.get(normalizeSoc(socCode));
    if (match) return match;
  }
  if (title) {
    return bySlug.get(titleToSlug(title));
  }
  return undefined;
}

export function isAiEconomyOccupation(socCode?: string, title?: string): boolean {
  return !!getAiEconomyOccupation(socCode, title);
}

/** Headline stats used on the hub, homepage strip, and article. */
export const AI_ECONOMY_STATS = {
  /** BLS 2024-34: total economy projected employment growth. */
  totalEconomyGrowthPct: 3.1,
  /** BLS 2024-34: computer and mathematical occupations projected growth. */
  computerMathGrowthPct: 10.1,
  /** BLS 2024-34: data scientists projected growth (4th fastest overall). */
  dataScientistGrowthPct: 33.5,
  /** PwC AI Jobs Barometer (June 2026): wage premium for AI-skilled roles. */
  aiWagePremiumPct: 62,
  /** BLS 2024-34: projected new software developer jobs. */
  softwareDeveloperNewJobs: 267700,
} as const;
